import { useContext, useEffect, useState } from "react";
import { SearchType } from "../utils/constants2";
import { LanguageContext } from "../context/language";
import { LocationsContext } from "../context/locations";
import { SearchContext2 } from "../context/search";
import { DoSearch2, getLocationList } from "../services/restSearchService";
import { LocatorConfigurationContext } from "../context/locatorConfiguration/LocatorConfigurationContext";
import { useTracking } from "react-tracking";
import { interfaceUseTracking } from "../interfaces";
import { getCategoryNames } from "../utils";

export function useSearchConfiguration(
  onSuccess: () => void,
  configParameters: any
) {
  const [searchConfigurationState, setsearchConfigurationState] = useState({
    loaded: false,
    errors: "",
  });

  const { language } = useContext(LanguageContext);
  const { searchParameters, searchCounter, isLoading } =
    useContext(SearchContext2);
  const { locatorConfigurationIsLoading, interfaceConfiguration } = useContext(
    LocatorConfigurationContext
  );

  const {
    setLocations,
    clearLocations,
    setMapLocations,
    searchReqLogId,
    totalResults,
  } = useContext(LocationsContext);

  const clearSearch = () => {
    if (clearLocations) {
      clearLocations();
    }

    setsearchConfigurationState({
      loaded: true,
      errors: "",
    });
  };

  const { trackEvent } = useTracking<interfaceUseTracking>({
    googleAnalytic: interfaceConfiguration?.googleAnalyticsCode,
  });

  useEffect(() => {
    const categoriesName = getCategoryNames(
      searchParameters.categoryIDs,
      interfaceConfiguration?.interfaceCategoryFilterOptions
    );

    if (searchReqLogId > 0) {
      trackEvent({
        eventNameGA: "locator_search_results_summary",
        extraParameters: {
          search_request_id: searchReqLogId,
          total_results: totalResults,
          selected_categories: categoriesName,
          categoryIDs: searchParameters.categoryIDs,
          radius: searchParameters.radius,
          city: searchParameters.city,
          state: searchParameters.state,
          postalCode: searchParameters.postalCode,
          keyword: searchParameters.keyword,
        },
      });
    }
  }, [searchReqLogId]);

  useEffect(() => {
    let locations;

    if (locatorConfigurationIsLoading && !searchConfigurationState.loaded)
      return;

    clearLocations();

    setsearchConfigurationState({
      loaded: false,
      errors: "",
    });

    //Add availabilityLimiterIDsSplit separate by coma
    const availabilityLimiterIDsSplit =
      interfaceConfiguration?.availabilityLimiters
        ? interfaceConfiguration.availabilityLimiters
            .map((limiter) => limiter.id)
            .join(",")
        : "";

    const localConfigParameters: any = {
      ...configParameters,
      AvailabilityLimiterIDs: availabilityLimiterIDsSplit,
      internetOnlySearch: interfaceConfiguration?.internetOnlySearch,
      findNearestForNoResults: interfaceConfiguration?.findNearestLocation,
      returnEvent: interfaceConfiguration?.showEvents,
      returnCoupon: interfaceConfiguration?.showCoupons,
      pageSize: process.env.REACT_APP_MAX_RESULTS_LIST,
      configClientSearch: {
        clientId: interfaceConfiguration?.clientId,
        apiKey: interfaceConfiguration?.apiKey,
      },
      getHoursForUpcomingWeek: true,
      startIndex: 0,
      languageCode: language,
      InterfaceID: interfaceConfiguration?.currentInterfaceConfigurationID,
      fillAttr: interfaceConfiguration?.showAttributes,
      showAllLocationsPerCountry:
        interfaceConfiguration?.showAllLocationsPerCountry,
      showNearestLocationsInList:
        interfaceConfiguration?.showNearestLocationsInList,
    };

    if (interfaceConfiguration?.internetOnlySearch) {
      delete localConfigParameters.internetOnlySearch;
      localConfigParameters.searchTypeOverride = 7;
    }

    // Enable cross-country borders search
    if (
      interfaceConfiguration?.crossCountrySearch &&
      searchParameters.searchTypeId === SearchType.radius
    ) {
      localConfigParameters.SearchTypeOverride = 1;
      localConfigParameters.CountryScope = "All";
    }

    if (interfaceConfiguration?.showEvents) {
      delete localConfigParameters.showEvents;

      localConfigParameters.returnEvent = true;
    }

    if (
      interfaceConfiguration?.showAllLocationsPerCountry &&
      !interfaceConfiguration?.showNearestLocationsInList &&
      !localConfigParameters?.manualSearching
    ) {
      delete localConfigParameters.showAllLocationsPerCountry;
      delete localConfigParameters.showNearestLocationsInList;

      localConfigParameters.maxResults = process.env.REACT_APP_MAX_RESULTS_LIST;
    }

    delete searchParameters.locationGroupIds;

    if (
      searchParameters.categoryIDs &&
      searchParameters.categoryIDs.length > 0
    ) {
      localConfigParameters.categoryIDs = searchParameters.categoryIDs;
    }

    const doSearch2 = async () => {
      // Resto del código de doSearch...
      try {
        locations = await DoSearch2({
          ...searchParameters,
          ...localConfigParameters,
        });

        if (setLocations && interfaceConfiguration) {
          setLocations(locations, interfaceConfiguration);
        }

        if (
          setMapLocations &&
          !(
            (interfaceConfiguration?.showAllLocations ||
              localConfigParameters?.enabledDisplayAllLocationsOnMap) &&
            searchCounter === 1
          ) &&
          !interfaceConfiguration?.showAllLocationsPerCountry
        ) {
          setMapLocations(locations);
        }

        if (
          (interfaceConfiguration?.showAllLocations ||
            localConfigParameters?.enabledDisplayAllLocationsOnMap) &&
          searchCounter === 1
        ) {
          await getAllLocation(true, false);
        }

        setsearchConfigurationState({
          loaded: true,
          errors: "",
        });
      } catch (error: any) {
        console.error("DoSearch2", error);

        setsearchConfigurationState({
          loaded: true,
          errors: error.response?.headers.errormessage,
        });
      }
    };

    const getAllLocation = async (
      showAllLocationsInMap: boolean = false,
      showAllLocationsInList: boolean = false
    ) => {
      try {
        const categories = (window as any).bullseyeLocationsConfig
          ?.filteredCategories
          ? (window as any).bullseyeLocationsConfig.filteredCategories
          : configParameters.filteredCategories;

        let countryId: string | undefined | null;

        countryId = searchParameters.countryId?.toString();

        if (
          ((interfaceConfiguration?.showAllLocations ||
            localConfigParameters?.enabledDisplayAllLocationsOnMap) &&
            (!interfaceConfiguration?.showAllLocationsPerCountry ||
              searchParameters.countryId?.toString() === "")) ||
          (searchCounter === 1 &&
            (interfaceConfiguration?.showAllLocations ||
              localConfigParameters?.enabledDisplayAllLocationsOnMap))
        ) {
          countryId = interfaceConfiguration?.countries
            .map((x) => x.id)
            .join(",");
        }

        const categoryLimiter =
          localConfigParameters?.CategoryLimiterIDs.length > 0
            ? localConfigParameters?.CategoryLimiterIDs
            : null;

        const locations = await getLocationList(
          interfaceConfiguration?.clientId,
          interfaceConfiguration?.apiKey,
          countryId,
          "json",
          null,
          null,
          null,
          null,
          categoryLimiter,
          null
        );

        const result = {
          ResultList: locations.locations,
          TotalResults: locations.locations.length,
        };

        if (
          (interfaceConfiguration && showAllLocationsInList) ||
          (interfaceConfiguration?.showAllLocationsPerCountry &&
            !interfaceConfiguration?.showNearestLocationsInList)
        ) {
          setLocations(
            {
              DistanceIsMetric: false,
              FindNearestSearch: false,
              ResultList: result.ResultList,
              SearchReqLogId: 0,
              TotalResults: result.TotalResults,
            },
            interfaceConfiguration
          );
        }
        if (showAllLocationsInMap) {
          setMapLocations({
            DistanceIsMetric: false,
            FindNearestSearch: false,
            ResultList: result.ResultList,
            SearchReqLogId: 0,
            TotalResults: result.TotalResults,
          });
        }

        setsearchConfigurationState({
          loaded: true,
          errors: "",
        });
      } catch (error: any) {
        setsearchConfigurationState({
          loaded: true,
          errors: "",
        });
      }
    };

    const search = async (defaultSearch: boolean = true) => {
      if (defaultSearch) {
        if (
          searchParameters.countryId &&
          searchParameters.ready &&
          (searchParameters.countryId ||
            searchParameters.state ||
            searchParameters.city ||
            searchParameters.postalCode) &&
          interfaceConfiguration?.clientId! > 0 &&
          interfaceConfiguration?.apiKey
        ) {
          await doSearch2();
        } else if (
          searchParameters.latitude &&
          searchParameters.longitude &&
          searchParameters.latitude !== 0 &&
          searchParameters.longitude !== 0 &&
          searchParameters.ready
        ) {
          await doSearch2();
        } else if (searchParameters.ready && searchParameters.keyword) {
          searchParameters.countryId = searchParameters.countryId;

          await doSearch2();
        } else if (
          (interfaceConfiguration?.showAllLocations ||
            localConfigParameters?.enabledDisplayAllLocationsOnMap) &&
          searchCounter === 1
        ) {
          await getAllLocation(true, false);
        } else {
          clearSearch();
        }
      } else {
        if (
          (interfaceConfiguration?.showAllLocations ||
            localConfigParameters?.enabledDisplayAllLocationsOnMap) &&
          searchCounter === 1
        ) {
          await getAllLocation(true, false);
        } else {
          if (
            interfaceConfiguration?.showAllLocationsPerCountry &&
            !interfaceConfiguration.showNearestLocationsInList
          ) {
            if (
              searchParameters.countryId &&
              searchParameters.countryId.toString() !== ""
            ) {
              if (
                searchParameters.postalCode ||
                searchParameters.state ||
                searchParameters.city ||
                SearchType.country === searchParameters.searchTypeId
              ) {
                await doSearch2();
              }

              if (!searchParameters.isManualSearch) {
                await getAllLocation(true, false);
              }
            }
          } else if (
            interfaceConfiguration?.showAllLocationsPerCountry &&
            interfaceConfiguration.showNearestLocationsInList
          ) {
            if (
              searchParameters.countryId &&
              searchParameters.countryId.toString() !== ""
            ) {
              if (
                searchParameters.postalCode ||
                searchParameters.state ||
                searchParameters.city ||
                SearchType.country === searchParameters.searchTypeId
              ) {
                await doSearch2();
              }
              if (!searchParameters.isManualSearch) {
                await getAllLocation(true, false);
              }
            }
          } else {
            await doSearch2();
          }
        }
      }
    };

    if (
      interfaceConfiguration?.crossCountrySearch &&
      !interfaceConfiguration?.showAllLocationsPerCountry
    ) {
      search(true);
    } else {
      if (
        (searchParameters.searchTypeId === SearchType.country ||
          searchParameters.searchTypeId === SearchType.radius) &&
        interfaceConfiguration?.showAllLocationsPerCountry &&
        !(
          searchCounter === 1 &&
          (interfaceConfiguration?.showAllLocations ||
            localConfigParameters?.enabledDisplayAllLocationsOnMap)
        )
      ) {
        search(false);
      } else {
        search(true);
      }
    }
  }, [searchParameters, onSuccess]);

  return [searchConfigurationState, clearSearch];
}
